import { useMemo } from 'react';
import {
  AccessorKeyColumnDef,
  ColumnFiltersState,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import {
  TicketDto,
  TicketStatus,
  PriorityLevel,
  TicketCategory,
  useOrganizationTicketsList,
  PaginationOptionsWithSortArray,
} from '@sib/api';
import { Button, FilterType, TableBadge, useTableSettings } from '@sib/ui';
import { snakeToTitle } from '@sib/utils';
import { CopyToClipboard } from '@spendbrain/common';
import {
  DialogScreen,
  useDialogStore,
} from '@spendbrain/common/stores/dialog-store';
import { NO_DATA_PLACEHOLDER } from '@spendbrain/config/constants';
import { SearchIcon, UserIcon } from '@spendbrain/icons';
import { DOCUMENTS_DATE_FORMAT } from '@spendbrain/modules/documents/utils';
import { TicketStatusBadge } from '@spendbrain/modules/tickets/components';
import { useAppTranslation } from '@spendbrain/modules/tickets/hooks';
import { TicketTabs } from '@spendbrain/types/tickets';
import { format } from 'date-fns';

const COLUMN_ID_MAP: Record<string, string> = {
  requestor_name: 'requestorName',
} as const;

const usePriorityLabelKeys = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => ({
      [PriorityLevel.Low]: t('pages.tickets.priority.low'),
      [PriorityLevel.Medium]: t('pages.tickets.priority.medium'),
      [PriorityLevel.High]: t('pages.tickets.priority.high'),
    }),
    [t]
  );
};

export const useStatusLabelKeys = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => ({
      [TicketStatus.New]: t('pages.tickets.status.new'),
      [TicketStatus.InProgress]: t('pages.tickets.status.inProgress'),
      [TicketStatus.Closed]: t('pages.tickets.status.closed'),
      [TicketStatus.WorkingWithVendor]: t(
        'pages.tickets.status.workingWithVendor'
      ),
      [TicketStatus.AwaitingClientAction]: t(
        'pages.tickets.status.awaitingClientAction'
      ),
      [TicketStatus.AwaitingCcaAction]: t(
        'pages.tickets.status.awaitingCcaAction'
      ),
      [TicketStatus.Canceled]: t('pages.tickets.status.canceled'),
      [TicketStatus.Completed]: t('pages.tickets.status.completed'),
    }),
    [t]
  );
};

export const statusColors: Record<TicketStatus, string> = {
  [TicketStatus.New]: '#ACEBD3',
  [TicketStatus.InProgress]: '#F7D49C',
  [TicketStatus.Closed]: '#D6D6D6',
  [TicketStatus.WorkingWithVendor]: '#D2DFFD',
  [TicketStatus.AwaitingClientAction]: '#D2DFFD',
  [TicketStatus.AwaitingCcaAction]: '#D2DFFD',
  [TicketStatus.Canceled]: '#FFCCD8',
  [TicketStatus.Completed]: '#ACEBD3',
};

interface UseTicketsProps extends PaginationOptionsWithSortArray {
  organizationId: string;
  searchTerm?: string;
}

export const useTickets = ({
  organizationId,
  filter,
  sort,
  page,
  size,
  searchTerm,
}: UseTicketsProps) => {
  const {
    data,
    isLoading,
    isFetching,
    isRefetching,
    refetch: refetchTickets,
  } = useOrganizationTicketsList(organizationId, {
    page,
    size,
    sort,
    filter,
    searchTerm,
  });

  return {
    refetchTickets,
    data: data?.items || [],
    isLoading: isLoading || isFetching || isRefetching,
    rowCount: data?.totalItems || 0,
  };
};

const useTicketStatusOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      { value: TicketStatus.New, label: t('pages.tickets.status.new') },
      {
        value: TicketStatus.InProgress,
        label: t('pages.tickets.status.inProgress'),
      },
      { value: TicketStatus.Closed, label: t('pages.tickets.status.closed') },
      {
        value: TicketStatus.WorkingWithVendor,
        label: t('pages.tickets.status.workingWithVendor'),
      },
      {
        value: TicketStatus.AwaitingClientAction,
        label: t('pages.tickets.status.awaitingClientAction'),
      },
      {
        value: TicketStatus.AwaitingCcaAction,
        label: t('pages.tickets.status.awaitingCcaAction'),
      },
      {
        value: TicketStatus.Canceled,
        label: t('pages.tickets.status.canceled'),
      },
      {
        value: TicketStatus.Completed,
        label: t('pages.tickets.status.completed'),
      },
    ],
    [t]
  );
};

const useTicketTypeOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      {
        value: TicketCategory.Account,
        label: t('pages.tickets.type.account'),
      },
      {
        value: TicketCategory.Baselines,
        label: t('pages.tickets.type.baselines'),
      },
      {
        value: TicketCategory.Client,
        label: t('pages.tickets.type.client'),
      },
      {
        value: TicketCategory.Contract,
        label: t('pages.tickets.type.contract'),
      },
      {
        value: TicketCategory.CustomerService,
        label: t('pages.tickets.type.customerService'),
      },
      {
        value: TicketCategory.Escalation,
        label: t('pages.tickets.type.escalation'),
      },
      {
        value: TicketCategory.Exceptions,
        label: t('pages.tickets.type.exceptions'),
      },
      {
        value: TicketCategory.Finance,
        label: t('pages.tickets.type.finance'),
      },
      {
        value: TicketCategory.FollowUp,
        label: t('pages.tickets.type.followUp'),
      },
      {
        value: TicketCategory.InformationRequest,
        label: t('pages.tickets.type.informationRequest'),
      },
      {
        value: TicketCategory.Invoice,
        label: t('pages.tickets.type.invoice'),
      },
      {
        value: TicketCategory.MasterData,
        label: t('pages.tickets.type.masterData'),
      },
      {
        value: TicketCategory.Negotiation,
        label: t('pages.tickets.type.negotiation'),
      },
      {
        value: TicketCategory.Others,
        label: t('pages.tickets.type.others'),
      },
      {
        value: TicketCategory.Payments,
        label: t('pages.tickets.type.payments'),
      },
      {
        value: TicketCategory.Property,
        label: t('pages.tickets.type.property'),
      },
      {
        value: TicketCategory.ServiceRequest,
        label: t('pages.tickets.type.serviceRequest'),
      },
      {
        value: TicketCategory.Transitions,
        label: t('pages.tickets.type.transitions'),
      },
      {
        value: TicketCategory.VendorLocal,
        label: t('pages.tickets.type.vendorLocal'),
      },
      {
        value: TicketCategory.MissingBill,
        label: t('pages.tickets.type.missingBill'),
      },
      {
        value: TicketCategory.UpdateMailingAddress,
        label: t('pages.tickets.type.updateMailingAddress'),
      },
      {
        value: TicketCategory.PotentialLeak,
        label: t('pages.tickets.type.potentialLeak'),
      },
      {
        value: TicketCategory.InvoiceQuestion,
        label: t('pages.tickets.type.invoiceQuestion'),
      },
    ],
    [t]
  );
};

export const useTicketPriorityOptions = () => {
  const { t } = useAppTranslation();

  return useMemo(
    () => [
      { value: PriorityLevel.Low, label: t('pages.tickets.priority.low') },
      {
        value: PriorityLevel.Medium,
        label: t('pages.tickets.priority.medium'),
      },
      { value: PriorityLevel.High, label: t('pages.tickets.priority.high') },
    ],
    [t]
  );
};

export type TicketsTableSettings = ReturnType<
  typeof useTableSettings<TicketDto>
>;

export const useTicketSortMapping = (sortState: SortingState) =>
  useMemo(
    () =>
      sortState.map(({ id, desc }) => ({
        id: COLUMN_ID_MAP[id] ?? id,
        desc,
      })),
    [sortState]
  );

export const useTicketFilterMapping = (filterState: ColumnFiltersState) =>
  useMemo(
    () =>
      filterState.map(({ id, value }) => ({
        id: COLUMN_ID_MAP[id] ?? id,
        value,
      })),
    [filterState]
  );

export const useTicketsColumns = (activeTab: string) => {
  const { t } = useAppTranslation();

  const columnHelper = createColumnHelper<TicketDto>();
  const { open: openTicketDetailsDialog } = useDialogStore();

  const statusOptions = useTicketStatusOptions();
  const typeOptions = useTicketTypeOptions();
  const priorityOptions = useTicketPriorityOptions();
  const priorityLabelKeys = usePriorityLabelKeys();
  const statusLabelKeys = useStatusLabelKeys();

  const statusOptionsByActiveTab = useMemo(
    () =>
      activeTab === TicketTabs.Actual
        ? statusOptions.filter(
            status =>
              status.value !== TicketStatus.Closed &&
              status.value !== TicketStatus.Canceled &&
              status.value !== TicketStatus.Completed
          )
        : statusOptions.filter(
            status =>
              status.value === TicketStatus.Closed ||
              status.value === TicketStatus.Canceled ||
              status.value === TicketStatus.Completed
          ),
    [activeTab, statusOptions]
  );

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('vectorTicketId', {
          header: t('pages.tickets.columns.number'),
          meta: {
            filter: FilterType.NUMBER,
            className: 'min-w-[150px]',
          },
          cell: info => {
            const name = info.getValue() || NO_DATA_PLACEHOLDER;

            return (
              <CopyToClipboard value={name.toString()}>
                <span className="text-primary font-bold">{name}</span>
              </CopyToClipboard>
            );
          },
        }),
        columnHelper.accessor('status', {
          header: t('pages.tickets.columns.status'),
          meta: {
            filter: FilterType.SELECT,
            filterOptions: statusOptionsByActiveTab,
            filterProps: {
              popoverProps: { contentClassName: 'min-w-[200px] max-w-[200px]' },
            },
            className: 'min-w-[150px] w-[150px]',
          },
          cell: info => {
            const value = info.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            return (
              <TableBadge style={{ backgroundColor: statusColors[value] }}>
                {statusLabelKeys[value]}
              </TableBadge>
            );
          },
        }),
        columnHelper.accessor('subStatus', {
          header: t('pages.tickets.columns.subStatus'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('category', {
          header: t('pages.tickets.columns.type'),
          meta: {
            filter: FilterType.SELECT,
            filterOptions: typeOptions,
            className: 'min-w-[150px]',
            filterProps: {
              popoverProps: { contentClassName: 'min-w-[220px] max-w-[220px]' },
            },
          },
          cell: info => {
            const value = info.getValue();
            const ticketTypeLabel =
              typeOptions.find(option => option.value === value)?.label ||
              snakeToTitle(value || '');

            return ticketTypeLabel ? (
              <TableBadge>{ticketTypeLabel}</TableBadge>
            ) : (
              NO_DATA_PLACEHOLDER
            );
          },
        }),
        columnHelper.accessor('subject', {
          header: t('pages.tickets.columns.subject'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('requestor.name', {
          header: t('pages.tickets.columns.reporter'),

          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
            filterProps: {
              inputClassName: 'max-w-[210px]',
            },
          },
          cell: info => (
            <span className="flex items-center gap-2">
              <UserIcon className="text-primary" />
              {info.getValue()?.trim() || NO_DATA_PLACEHOLDER}
            </span>
          ),
        }),
        columnHelper.accessor('priority', {
          header: t('pages.tickets.columns.priority'),
          meta: {
            filter: FilterType.SELECT,
            filterOptions: priorityOptions,
            className: 'min-w-[150px]',
          },
          cell: info => {
            const value = info.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            const priorityOption = priorityOptions.find(
              option => option.value === value
            );

            return (
              <TableBadge>
                {priorityOption?.label ||
                  priorityLabelKeys[value] ||
                  NO_DATA_PLACEHOLDER}
              </TableBadge>
            );
          },
        }),
        columnHelper.accessor('vendorName', {
          header: t('pages.tickets.columns.vendor'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('location', {
          header: t('pages.tickets.columns.location'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),

        columnHelper.accessor('address', {
          header: t('pages.tickets.columns.address'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('city', {
          header: t('pages.tickets.columns.city'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('state', {
          header: t('pages.tickets.columns.state'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('zip', {
          header: t('pages.tickets.columns.zip'),
          meta: {
            filter: FilterType.TEXT,
            className: 'min-w-[150px]',
          },
          cell: info => info.getValue() || NO_DATA_PLACEHOLDER,
        }),
        columnHelper.accessor('createdAt', {
          header: t('pages.tickets.columns.created'),
          meta: {
            filter: FilterType.DATE,
            className: 'min-w-[165px]',
          },
          cell: cell => {
            const value = cell.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            return format(value, DOCUMENTS_DATE_FORMAT);
          },
        }),
        columnHelper.accessor('updatedAt', {
          header: t('pages.tickets.columns.lastUpdate'),
          meta: {
            filter: FilterType.DATE,
            className: 'min-w-[165px]',
          },
          cell: cell => {
            const value = cell.getValue();
            if (!value) return NO_DATA_PLACEHOLDER;

            return format(value, DOCUMENTS_DATE_FORMAT);
          },
        }),
        {
          id: 'actions',
          header: () => (
            <span className="flex w-full justify-end">
              {t('pages.tickets.actions.more')}
            </span>
          ),
          enableSorting: false,
          cell: ({ row }) => {
            const org = row.original;
            const statusText = statusOptions.find(
              status => status.value === org.status
            )?.label;

            return (
              <div className="flex items-center justify-end gap-2">
                <Button
                  variant="ghost"
                  onClick={() => {
                    openTicketDetailsDialog({
                      component: DialogScreen.TicketDetails,
                      componentProps: { ticketId: org.id },
                      dialogTitle: (
                        <div className="flex items-center gap-2">
                          <span>
                            {t('components.ticketDetails.title', {
                              number: org.vectorTicketId,
                            })}
                          </span>
                          {org?.status && (
                            <TicketStatusBadge
                              color={statusColors[org.status]}
                              text={statusText}
                            />
                          )}
                        </div>
                      ),
                    });
                  }}
                  className="text-primary size-6 p-0"
                >
                  <SearchIcon />
                </Button>
              </div>
            );
          },
          meta: {
            className:
              'sticky right-0 z-20 min-w-[90px] !rounded-none group-odd:bg-white group-even:bg-[#F6F8FA] group-hover:bg-[#EDF2F7] transition-all duration-200',
          },
        },
      ] as AccessorKeyColumnDef<TicketDto>[],
    [
      columnHelper,
      t,
      statusOptions,
      typeOptions,
      priorityOptions,
      statusLabelKeys,
      priorityLabelKeys,
      openTicketDetailsDialog,
      statusOptionsByActiveTab,
    ]
  );

  return columns;
};
